<template>
  <div>
    <page-header
      :page="page"
      left-side-class="col-xl-8"
      :actions="headerActions"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <filter-manager
      ref="filter"
      v-model="filter.model"
      :filters="filtersHeader"
      class="filter-manager_extended"
      search-container-class="search-btn"
      @academicYear="getMunicipalities"
      @municipality="getSchools"
      @search="search()"
    />
    <custom-separator class="my-3 custom-separator" text="Resultados" />
    <div v-if="hasResults">
      <result-row
        v-for="(item, index) in results"
        :key="index"
        :item="item"
        @open="handlerOpen"
        @close="handlerClose"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onPageChange"
      />
    </div>
    <custom-result-not-found v-else text="No hay Información Disponible." />
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import FilterManager from '@/components/common/CustomFilterManager.vue';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';

import mixAlert from '@/mixins/alert.mixin';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';

import apiService from '@/services/api/requisitionmanagment.api';
import referralAndEquipmentsService from '@/services/api/referralsandequipments.api';
import locationService from '@/services/api/location.api';
const MODULE_NAME = 'RequisitionManagement';

const DEFAULT_PAGE_SIZE = 10;

export default {
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
  },
  mixins: [mixAlert, storeMixin],
  data: () => ({
    moduleName: MODULE_NAME,
    page: {
      icon: 'fas fa-list',
      title: 'Manejo de Requisiciones',
      routes: ['Asistencia Tecnológica', 'Manejo de Requisiciones'],
    },
    results: [],
    filter: {
      model: {
        academicYear: null,
        status: null,
        referralOrRequisitionNumber: null,
        referralType: null,
        caat: null,
        municipality: null,
        school: null,
      },
      options: {
        academicYears: [],
        statuses: [],
        referralTypes: null,
        caats: null,
        municipalities: null,
        schools: [],
      },
    },
    pagination: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  }),
  computed: {
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.push({ name: 'at' }),
        },
      ];
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filter.options.academicYears,
          value: this.filter.model.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: true,
          error: 'El año académico es requerido.',
          validations: 'required',
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
          classContainer: 'w-100',
        },
        {
          name: 'referralOrRequisitionNumber',
          component: 'CustomInput',
          value: this.filter.model.referralOrRequisitionNumber,
          placeholder:
            'Escriba Número de Referido | Número de Requisición | Número de SIE',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-sm-12 col-md',
          type: 'text',
        },
        {
          name: 'referralType',
          label: 'Tipo de Referido:',
          component: 'CustomDropDown',
          value: this.filter.model.referralType,
          options: this.filter.options.referralTypes,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
        },
        {
          name: 'caat',
          label: 'CAAT:',
          component: 'CustomDropDown',
          value: this.filter.model.caat,
          options: this.filter.options.caats,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          disabled: this.filter.options.caats?.length === 1,
        },
        {
          name: 'municipality',
          label: 'Municipio:',
          component: 'CustomDropDown',
          value: this.filter.model.municipality,
          options: this.filter.options.municipalities,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          disabled: this.filter.options.municipalities?.length === 1,
        },
        {
          name: 'school',
          label: 'Escuela:',
          component: 'CustomDropDown',
          value: this.filter.model.school,
          options: this.filter.options.schools,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          disabled:
            this.filter.options.schools?.length === 1 ||
            !this.filter.model.municipality,
        },
      ];
    },
    hasResults() {
      return Boolean(this.results?.length);
    },
  },
  async mounted() {
    await Promise.all([
      this.getAcademicYears(),
      this.getStatuses(),
      this.getReferralTypes(),
      this.getCaats(),
    ]);
  },
  methods: {
    async getAcademicYears() {
      const { data } = await apiService.getAcademicyears();
      this.filter.options.academicYears = data;
      this.filter.model.academicYear = data[0];
      this.getMunicipalities();
    },
    async getStatuses() {
      const { data } = await apiService.getStatuses();
      this.filter.options.statuses = data;
      if (data.length === 1) this.filter.model.status = data[0];
    },
    async getReferralTypes() {
      const { data } = await referralAndEquipmentsService.getServiceTypes();
      this.filter.options.referralTypes = data;
    },
    async getCaats() {
      const { data } = await locationService.getCaats();
      this.filter.options.caats = data;
      if (data.length === 1) this.filter.model.caat = data[0];
    },
    async getMunicipalities() {
      this.filter.options.schools = [];
      this.filter.options.municipalities= [];
      this.filter.model.school=null;
      this.filter.model.municipality=null;
      if(this.filter.model.academicYear==null){
        return;
      }

      const { data } = await locationService.getMunicipalities(
        this.filter.model.academicYear.id
      );
      this.filter.options.municipalities = data;
      if (data.length === 1) this.filter.model.municipality = data[0];
    },
    async getSchools(municipalitiy = null) {
      this.filter.model.school = null;
      this.filter.options.schools = [];
      if (!municipalitiy) {
        return;
      }
      const { data } = await locationService.getSchools(
        this.filter.model.academicYear.id,
        this.filter.model.municipality.id
      );
      this.filter.options.schools = data;
      if (data.length === 1) this.filter.model.school = data[0];
    },
    async search(page = 1) {
      const {
        academicYear,
        status,
        referralOrRequisitionNumber,
        referralType,
        caat,
        municipality,
        school,
      } = this.filter.model;

      const { data: result } = await apiService.search({
        academicYearId: academicYear?.id,
        statusId: status?.id,
        referralTypeId: referralType?.id,
        caatId: caat?.id,
        municipalityId: municipality?.id,
        schoolId: school?.id,
        referralOrRequisitionNumber: referralOrRequisitionNumber,
        currentPage: page,
        pageSize: this.pagination.pageSize,
      });

      this.results = result.data.map((x) => ({
        ...x,
        isOpen: false,
        referralDocuments: [],
      }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: result.totalItemCount,
        pageCount: result.pageCount,
        currentPage: result.currentPage,
        count: result.count,
      };
    },
    async onPageChange(page) {
      this.search(page);
    },
    handlerOpen(referral) {
      this.results
        .filter((x) => x.id !== referral.id && x.isOpen)
        .forEach((x) => (x.isOpen = false));
      referral.isOpen = true;
    },
    handlerClose(referral) {
      referral.isOpen = false;
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const routes = ['RequisitionManagementDetail'];
    next(async (vm) => {
      if (routes.includes(from.name)) {
        vm.recoveryData();
        await vm.onPageChange(1);
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
};
</script>
<style lang="scss" scoped>
.custom-separator ::v-deep > .col-12 {
  padding: 0 !important;
}
</style>
